import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

// Reutilizamos la interfaz
interface LimpiezaExpressData {
    id: number;
    id_habitacion: number;
    estado: "Pendiente" | "En Proceso" | "Terminado";
    fecha_inicio: string | null;
    fecha_fin: string | null;
    notas: string;
    habitacion: {
        id: number;
        nombre: string;
        banner?: string;
    };
}

export const LimpiezaExpress = () => {
    // Obtenemos el ID de la limpieza desde la URL
    const { idLimpieza } = useParams();

    const [limpieza, setLimpieza] = useState<LimpiezaExpressData | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [notas, setNotas] = useState("");

    const fetchDatosLimpieza = async () => {
        try {
            // Asegúrate de que este endpoint no pida token de autenticación (o crea uno público específico para esto)
            const { data } = await axios.get(
                `${process.env.REACT_APP_PROXY}/limpieza_habitaciones/${idLimpieza}`,
            );
            setLimpieza(data);
            if (data.notas) setNotas(data.notas);
        } catch (error) {
            console.error("Error obteniendo datos", error);
            Swal.fire(
                "Error",
                "No se pudo cargar la información de la habitación",
                "error",
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (idLimpieza) {
            fetchDatosLimpieza();
        }
    }, [idLimpieza]);

    const handleAccion = async (accion: "INICIAR" | "TERMINAR") => {
        setActionLoading(true);
        try {
            // Este endpoint debe permitir actualizar sin token si viene desde el enlace mágico,
            // o puedes pasar un token temporal en la URL
            await axios.put(
                `${process.env.REACT_APP_PROXY}/limpieza_habitaciones/gestionar/${idLimpieza}`,
                {
                    accion,
                    notas: accion === "TERMINAR" ? notas : null,
                    // Si no hay usuario logueado, puedes mandar un ID genérico o null si el backend lo permite
                    id_usuario: null,
                },
            );

            Swal.fire({
                icon: "success",
                title:
                    accion === "INICIAR"
                        ? "¡Limpieza Iniciada!"
                        : "¡Habitación Lista!",
                text:
                    accion === "INICIAR"
                        ? "Se ha registrado tu hora de entrada."
                        : "Buen trabajo. La habitación está lista para el siguiente huésped.",
                timer: 3000,
                showConfirmButton: false,
            });

            // Refrescamos los datos para que el botón cambie
            fetchDatosLimpieza();
        } catch (error) {
            Swal.fire("Error", "Ocurrió un problema al guardar", "error");
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <div className="w-12 h-12 border-4 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
                <p className="mt-4 text-gray-500 animate-pulse">
                    Cargando tarea...
                </p>
            </div>
        );
    }

    if (!limpieza) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <p className="text-xl text-red-500">
                    Enlace inválido o tarea no encontrada.
                </p>
            </div>
        );
    }

    // Colores de estado
    const estadoColors = {
        Pendiente: "bg-red-100 text-red-600 border-red-200",
        "En Proceso": "bg-yellow-100 text-yellow-700 border-yellow-200",
        Terminado: "bg-green-100 text-green-700 border-green-200",
    };

    const imagenHabitacion =
        limpieza.habitacion.banner ||
        "https://via.placeholder.com/400x200?text=Habitaci%C3%B3n";

    return (
        <div className="min-h-screen bg-gray-100 pb-10">
            {/* Header simple */}
            <div className="bg-[#029BFC] text-white p-4 shadow-md text-center">
                <h1 className="text-xl font-bold tracking-wider">BLUSTAY</h1>
            </div>

            <div className="max-w-md mx-auto mt-6 px-4">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    {/* Imagen de la habitación */}
                    <div className="relative h-48 bg-gray-200">
                        <img
                            src={
                                `${process.env.REACT_APP_PROXY}/hotel_habitaciones/getImage/` +
                                imagenHabitacion
                            }
                            alt="Habitación"
                            className="w-full h-full object-cover"
                        />
                        <div
                            className={`absolute top-4 right-4 px-3 py-1 font-bold rounded-full text-xs shadow-md border ${estadoColors[limpieza.estado]}`}
                        >
                            {limpieza.estado.toUpperCase()}
                        </div>
                    </div>

                    <div className="p-6">
                        <h2 className="text-2xl font-black text-gray-800 text-center mb-2">
                            {limpieza.habitacion.nombre}
                        </h2>
                        <p className="text-center text-gray-500 text-sm mb-8">
                            Tarea asignada #{limpieza.id}
                        </p>

                        {/* ACCIONES SEGÚN EL ESTADO */}
                        {limpieza.estado === "Pendiente" && (
                            <div className="flex flex-col gap-4">
                                <p className="text-center text-gray-600">
                                    Presiona el botón cuando vayas a entrar a la
                                    habitación.
                                </p>
                                <button
                                    onClick={() => handleAccion("INICIAR")}
                                    disabled={actionLoading}
                                    className="w-full py-4 text-lg font-black text-white bg-blue-600 rounded-xl shadow-lg hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
                                >
                                    {actionLoading
                                        ? "Cargando..."
                                        : "EMPEZAR LIMPIEZA"}
                                </button>
                            </div>
                        )}

                        {limpieza.estado === "En Proceso" && (
                            <div className="flex flex-col gap-4 animate-fade-in">
                                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl mb-2 text-center">
                                    <p className="text-sm text-yellow-800 font-semibold">
                                        Limpieza en curso...
                                    </p>
                                </div>

                                <label className="text-sm font-bold text-gray-700">
                                    Notas / Novedades (Opcional):
                                </label>
                                <textarea
                                    value={notas}
                                    onChange={(e) => setNotas(e.target.value)}
                                    placeholder="Ej: Faltan toallas, jabón, o hay algo dañado..."
                                    className="w-full p-3 border-2 border-gray-300 rounded-xl h-24 outline-none focus:border-[#029BFC] resize-none"
                                />

                                <button
                                    onClick={() => handleAccion("TERMINAR")}
                                    disabled={actionLoading}
                                    className="w-full py-4 text-lg font-black text-white bg-green-500 rounded-xl shadow-lg hover:bg-green-600 active:scale-95 transition-all mt-4 disabled:opacity-50"
                                >
                                    {actionLoading
                                        ? "Guardando..."
                                        : "TERMINAR LIMPIEZA"}
                                </button>
                            </div>
                        )}

                        {limpieza.estado === "Terminado" && (
                            <div className="flex flex-col items-center justify-center p-6 text-center">
                                <div className="w-16 h-16 mb-4 bg-green-100 text-green-500 rounded-full flex items-center justify-center">
                                    <svg
                                        className="w-8 h-8"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="3"
                                            d="M5 13l4 4L19 7"
                                        ></path>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800">
                                    ¡Trabajo finalizado!
                                </h3>
                                <p className="text-gray-500 mt-2">
                                    La habitación ya está disponible.
                                </p>
                                <p className="mt-4 text-xs text-gray-400 font-semibold uppercase">
                                    Ya puedes cerrar esta ventana
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
